/* Kit adapter for the existing S3 presigner, Uplink transform allowance and
   OpenAI image-edit proxy. No provider credentials belong in the browser. */
(() => {
  const storeGet = (key, session=false) => { try { return (session ? sessionStorage : localStorage).getItem(key); } catch { return null; } };
  const storeSet = (key,value) => { try { localStorage.setItem(key,value); } catch {} };
  async function fingerprint() {
    // Match the existing customizer identity instead of creating a new quota.
    const canvas=document.createElement('canvas');canvas.width=200;canvas.height=50;
    const ctx=canvas.getContext('2d');ctx.textBaseline='top';ctx.font='14px Arial';ctx.fillText('RoboPixel-FP-2024',2,2);ctx.fillStyle='#f60';ctx.fillRect(100,1,62,20);
    let hash=0;for(const chr of canvas.toDataURL()){hash=((hash<<5)-hash)+chr.charCodeAt(0);hash|=0;}
    const raw=Math.abs(hash)+'-'+screen.width+'x'+screen.height+'-'+navigator.language;
    return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256',new TextEncoder().encode(raw))),b=>b.toString(16).padStart(2,'0')).join('');
  }
  async function json(url,init={},signal,timeout=30000) {
    const response=await fetch(url,{...init,signal:signal ? AbortSignal.any([signal,AbortSignal.timeout(timeout)]) : AbortSignal.timeout(timeout)});
    let data;try{data=await response.json();}catch{throw Object.assign(new Error('Unavailable'),{code:'UNAVAILABLE'});}
    if(!response.ok) throw Object.assign(new Error('Request failed'),{code:data.code||data.error||'FAILED'});
    return data;
  }
  async function normalized(blob) {
    const image=await createImageBitmap(blob);
    try {
      if(image.width*image.height>50000000) throw new Error('Image too large');
      const scale=Math.min(1,2048/Math.max(image.width,image.height));
      const canvas=document.createElement('canvas');canvas.width=Math.max(1,Math.round(image.width*scale));canvas.height=Math.max(1,Math.round(image.height*scale));
      canvas.getContext('2d').drawImage(image,0,0,canvas.width,canvas.height);
      const result=await new Promise(resolve=>canvas.toBlob(resolve,blob.type==='image/jpeg'?'image/jpeg':'image/png',.94));
      if(!result||result.size>12*1024*1024) throw new Error('Image too large');
      return result;
    } finally { image.close(); }
  }
  let turnstileScript;
  async function human(sitekey,container,signal) {
    if(!window.turnstile) {
      turnstileScript ||= new Promise((resolve,reject)=>{const s=document.createElement('script');s.src='https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';s.onload=resolve;s.onerror=()=>{turnstileScript=null;reject(new Error('Security unavailable'));};document.head.append(s);});
      await turnstileScript;
    }
    signal?.throwIfAborted();
    return new Promise((resolve,reject)=>{
      let widget,done=false;
      const finish=(token)=>{if(done)return;done=true;clearTimeout(timer);signal?.removeEventListener('abort',abort);if(widget!==undefined)window.turnstile.remove(widget);token?resolve(token):reject(Object.assign(new Error('Security check failed'),{code:'SECURITY'}));};
      const abort=()=>finish();const timer=setTimeout(()=>finish(),60000);signal?.addEventListener('abort',abort,{once:true});
      try{widget=window.turnstile.render(container,{sitekey,action:'portrait_generate',appearance:'interaction-only',size:'compact',callback:finish,'error-callback':()=>finish(),'expired-callback':()=>finish()});}catch{finish();}
    });
  }
  class KitAPI {
    constructor(config){this.config=config;}
    async generate(input,progress,signal,container) {
      const c=this.config;
      // Read-only feature check comes before any photo upload or quota spend.
      let capability;try{capability=await json(c.proxy+'/kit-preview',{},signal);}catch{throw Object.assign(new Error('Not active'),{code:'KIT_UNAVAILABLE'});}
      if(input.metadata.set==='artwork'&&capability.artworkVersion!==1)throw Object.assign(new Error('Not active'),{code:'KIT_UNAVAILABLE'});
      if(capability.enabled!==true||capability.version!==3||capability.caseReferenceVersion!==1||capability.quality!=='high') throw Object.assign(new Error('Not active'),{code:'KIT_UNAVAILABLE'});
      const fp=await fingerprint();const vipCode=storeGet('robopixel_vip_code',true)||'';
      const quota=async(action,grant,requestSignal=signal)=>{
        const data=await json(c.uplink+'/generations/'+action,{method:'POST',credentials:'include',headers:{'Content-Type':'application/json'},body:JSON.stringify({type:'transform',fingerprint:fp,deviceToken:storeGet('robopixel_device_token'),vipCode,...(grant?{grant}:{})})},requestSignal);
        if(data.deviceToken)storeSet('robopixel_device_token',data.deviceToken);return data;
      };
      const allowance=await quota('check');
      if(allowance.allowed===false||allowance.remaining<1) throw Object.assign(new Error('Allowance used'),{code:'QUOTA'});
      progress('security');const token=await human(c.sitekey,container,signal);
      const upload=async(blob)=>{
        const clean=await normalized(blob);signal?.throwIfAborted();
        const signed=await json(c.storage+'/presign',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({folder:'transform-originals',contentType:clean.type})},signal);
        const expected='robopixel-designs-eu-121505946416-eu-central-1-an.s3.eu-central-1.amazonaws.com';
        for(const value of [signed.uploadUrl,signed.publicUrl]){const u=new URL(value);if(u.protocol!=='https:'||u.hostname!==expected)throw new Error('Invalid upload destination');}
        const response=await fetch(signed.uploadUrl,{method:'PUT',headers:{'Content-Type':clean.type},body:clean,signal:AbortSignal.any([signal,AbortSignal.timeout(60000)])});
        if(!response.ok)throw new Error('Upload failed');return signed.publicUrl;
      };
      progress('upload');const references=[];
      for(const reference of input.references) {
        if(reference.role==='blank_case' && reference.url) {
          const url=new URL(reference.url);
          if(url.protocol!=='https:'||url.hostname!=='cdn.shopify.com'||!url.pathname.startsWith('/s/files/1/0915/4297/8895/files/'))throw Object.assign(new Error('Invalid case reference'),{code:'CASE_REFERENCE'});
          references.push({role:reference.role,url:url.href});
        } else references.push({role:reference.role,url:await upload(reference.blob)});
      }
      signal?.throwIfAborted();
      let grant,received=false,dispatched=false;
      try{
        // Do not abort while acquiring a counted grant: retain it for refunds.
        const counted=await quota('increment',null,null);grant=counted.grant;
        if(!grant)throw new Error('Missing allowance');signal?.throwIfAborted();
        progress('generate');
        dispatched=true;
        const result=await json(c.proxy+'/kit-preview',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({token,grant,input:{...input.metadata,style:['Dynamic','Dynamik'].includes(input.metadata.style)?'Track':input.metadata.style,references}})},signal,270000);
        // The deployed proxy exposes a clean, display-only preview alongside
        // its protected persisted image. Keep the display copy in memory only.
        const display=result.outputClean||result.output?.[0];
        if(result.status!=='succeeded'||!/^data:image\/(jpeg|png|webp);base64,[A-Za-z0-9+/=]+$/.test(display||''))throw new Error('Invalid preview');
        received=true;
        return {url:display,assetId:result.assetId||'',references};
      }finally{
        if(grant&&!received){
          if(!dispatched){try{await json(c.proxy+'/kit-preview/cancel',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({grant})});}catch{}}
          try{await quota('refund',grant,null);}catch{/* Server receipts decide refunds; never mint local allowance. */}
        }
      }
    }
  }
  window.RoboPixelKitAPI=KitAPI;
})();
